import { Alert, Platform } from 'react-native';
import * as DocumentPicker from 'expo-document-picker';
import * as ImagePicker from 'expo-image-picker';
import * as FileSystem from 'expo-file-system/legacy';

import {
  isUnexpectedFailureHandled,
  markUnexpectedFailureHandled,
  presentUnexpectedFailure,
} from '../../sentry/presentUnexpectedFailure';

export type PickedAttachmentFile = {
  uri: string;
  fileName: string;
  mimeType: string;
  fileSize: number;
  base64: string;
};

const PICKER_TYPES = ['image/*', 'application/pdf'] as const;
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const ALLOWED_MIME_PREFIXES = ['image/'];
const ALLOWED_MIME_EXACT = ['application/pdf'];

function isAllowedMime(mime: string): boolean {
  if (ALLOWED_MIME_EXACT.includes(mime)) return true;
  return ALLOWED_MIME_PREFIXES.some((p) => mime.startsWith(p));
}

async function readFileAsBase64(uri: string): Promise<string> {
  if (Platform.OS === 'web') {
    const response = await fetch(uri);
    if (!response.ok) {
      throw new Error('No pude leer ese archivo.');
    }
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result === 'string') {
          const commaIdx = result.indexOf(',');
          resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
        } else {
          reject(new Error('No pude procesar ese archivo.'));
        }
      };
      reader.onerror = () => reject(new Error('No pude leer el archivo.'));
      reader.readAsDataURL(blob);
    });
  }
  return FileSystem.readAsStringAsync(uri, { encoding: 'base64' as const });
}

async function pickWithDocumentPicker(): Promise<PickedAttachmentFile | null> {
  const result = await DocumentPicker.getDocumentAsync({
    type: [...PICKER_TYPES],
    copyToCacheDirectory: Platform.OS !== 'web',
    multiple: false,
  });
  if (result.canceled || !result.assets?.[0]?.uri) {
    return null;
  }
  const asset = result.assets[0];
  const mimeType = asset.mimeType ?? 'application/octet-stream';
  const fileSize = asset.size ?? 0;

  if (!isAllowedMime(mimeType)) {
    throw new Error('Solo JPG, PNG o PDF.');
  }
  if (fileSize > MAX_FILE_SIZE) {
    throw new Error('El archivo no puede pesar más de 10 MB.');
  }

  const base64 = await readFileAsBase64(asset.uri);
  if (Platform.OS === 'web' && asset.uri.startsWith('blob:')) {
    URL.revokeObjectURL(asset.uri);
  }
  return {
    uri: Platform.OS === 'web' && asset.uri.startsWith('blob:') ? '' : asset.uri,
    fileName: asset.name ?? 'comprobante',
    mimeType,
    fileSize,
    base64,
  };
}

function pickWithWebInput(): Promise<PickedAttachmentFile | null> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      resolve(null);
      return;
    }
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*,.pdf,application/pdf';
    Object.assign(input.style, {
      position: 'fixed',
      top: '0',
      left: '0',
      width: '1px',
      height: '1px',
      opacity: '0',
      overflow: 'hidden',
    });

    let settled = false;
    const finish = (value: PickedAttachmentFile | null) => {
      if (settled) return;
      settled = true;
      input.remove();
      resolve(value);
    };

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        finish(null);
        return;
      }
      try {
        const mimeType = file.type || 'application/octet-stream';
        if (!isAllowedMime(mimeType)) {
          reject(new Error('Solo JPG, PNG o PDF.'));
          return;
        }
        if (file.size > MAX_FILE_SIZE) {
          reject(new Error('El archivo no puede pesar más de 10 MB.'));
          return;
        }
        const base64 = await new Promise<string>((res, rej) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result;
            if (typeof result === 'string') {
              const commaIdx = result.indexOf(',');
              res(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
            } else {
              rej(new Error('No pude procesar ese archivo.'));
            }
          };
          reader.onerror = () => rej(new Error('No pude leer el archivo.'));
          reader.readAsDataURL(file);
        });
        finish({
          uri: '',
          fileName: file.name,
          mimeType,
          fileSize: file.size,
          base64,
        });
      } catch (err) {
        reject(err instanceof Error ? err : new Error(String(err)));
      }
    };

    input.oncancel = () => finish(null);

    document.body.appendChild(input);
    try {
      if (typeof input.showPicker === 'function') {
        input.showPicker();
      } else {
        input.click();
      }
    } catch {
      input.click();
    }
  });
}

async function pickWithCamera(): Promise<PickedAttachmentFile | null> {
  const perm = await ImagePicker.requestCameraPermissionsAsync();
  if (!perm.granted) {
    throw new Error('Necesito permiso de cámara para sacar la foto.');
  }
  const result = await ImagePicker.launchCameraAsync({
    quality: 0.9,
    base64: false,
    allowsEditing: false,
  });
  if (result.canceled || !result.assets?.[0]?.uri) {
    return null;
  }
  const asset = result.assets[0];
  const mimeType = asset.mimeType ?? 'image/jpeg';
  const fileSize = asset.fileSize ?? 0;
  if (!isAllowedMime(mimeType)) {
    throw new Error('Solo JPG, PNG o PDF.');
  }
  if (fileSize > MAX_FILE_SIZE) {
    throw new Error('El archivo no puede pesar más de 10 MB.');
  }
  const base64 = await readFileAsBase64(asset.uri);
  return {
    uri: asset.uri,
    fileName: asset.fileName ?? `foto-${Date.now()}.jpg`,
    mimeType,
    fileSize,
    base64,
  };
}

function isExpectedPickError(e: unknown): boolean {
  const msg = e instanceof Error ? e.message : String(e);
  return msg.includes('Solo JPG') || msg.includes('10 MB') || msg.includes('no puede pesar');
}

type AttachmentSource = 'files' | 'camera';

async function pickBySource(source: AttachmentSource): Promise<PickedAttachmentFile | null> {
  if (source === 'camera') return pickWithCamera();
  if (Platform.OS === 'web') return pickWithWebInput();
  return pickWithDocumentPicker();
}

async function pickAttachmentWithErrorHandling(
  module: string,
  source: AttachmentSource
): Promise<PickedAttachmentFile | null> {
  // Web: DocumentPicker → blob: + fetch choca con CSP; el fallback pierde user activation.
  try {
    return await pickBySource(source);
  } catch (e) {
    if (isUnexpectedFailureHandled(e)) throw e;
    // Validación de tipo/tamaño: el usuario lo corrige; no es bug de producto.
    if (isExpectedPickError(e)) {
      throw e;
    }
    const userMessage = e instanceof Error ? e.message : 'No pude leer ese archivo.';
    presentUnexpectedFailure({
      module,
      op: Platform.OS === 'web' ? 'pick_attachment_web' : 'pick_attachment_native',
      error: e,
      title: 'Adjuntar comprobante',
      userMessage,
    });
    const err = e instanceof Error ? e : new Error(userMessage);
    throw markUnexpectedFailureHandled(err);
  }
}

/**
 * File picker genérico para comprobantes/adjuntos (imagen o PDF, máx 10 MB).
 * `module` etiqueta el contexto de observabilidad en Sentry.
 */
export async function pickAttachmentFile(module: string): Promise<PickedAttachmentFile | null> {
  return pickAttachmentWithErrorHandling(module, 'files');
}

/**
 * Variante interactiva: en nativo ofrece "Tomar foto" o "Elegir archivo"
 * (la foto del cheque/comprobante es el caso principal en móvil).
 * En web va directo al selector de archivos.
 */
export async function pickAttachmentInteractive(module: string): Promise<PickedAttachmentFile | null> {
  if (Platform.OS === 'web') {
    return pickAttachmentWithErrorHandling(module, 'files');
  }
  const source = await new Promise<AttachmentSource | null>((resolve) => {
    Alert.alert(
      'Adjuntar comprobante',
      undefined,
      [
        { text: 'Tomar foto', onPress: () => resolve('camera') },
        { text: 'Elegir archivo', onPress: () => resolve('files') },
        { text: 'Cancelar', style: 'cancel', onPress: () => resolve(null) },
      ],
      { cancelable: true, onDismiss: () => resolve(null) }
    );
  });
  if (!source) return null;
  return pickAttachmentWithErrorHandling(module, source);
}
